import { createApi } from '@reduxjs/toolkit/query/react';
import { graphqlRequestBaseQuery } from '@rtk-query/graphql-request-base-query';
import { client } from 'graphql/client';
import { createFleet, updateFleetById } from 'graphql/mutations';
import { getFleetById, listFleets } from 'graphql/queries';
import { z } from 'zod';

import { errorHandler } from '../errors';

import type { CreateFleetInput, SystemFleet, SystemFleetDetail, UpdatedFleetInput } from 'API';
import { SystemFleetDetailSchema, SystemFleetSchema } from 'APIzod';

const listSystemFleetsQueryFn = async () => {
    try {
        const { data } = await client.graphql({
            query: listFleets,
        });
        return { data: z.array(SystemFleetSchema()).parse(data.listFleets) };
    } catch (error) {
        throw errorHandler('Unexpected error occurred while fetching system fleets', error);
    }
};

const getSystemFleetAttributeQueryFn = async (id: string) => {
    try {
        const { data } = await client.graphql({ query: getFleetById, variables: { id } });
        return { data: SystemFleetDetailSchema().parse(data.getFleetById) };
    } catch (error) {
        throw errorHandler(`Unexpected error occurred while fetching system fleet ${id} attributes`, error);
    }
};

export const systemFleetsApi = createApi({
    reducerPath: 'systemFleets',
    baseQuery: graphqlRequestBaseQuery({
        url: '/graphql',
    }),
    refetchOnMountOrArgChange: true,
    endpoints: builder => ({
        listSystemFleets: builder.query<SystemFleet[], string>({
            queryFn: listSystemFleetsQueryFn,
        }),
        getSystemFleetsAttributes: builder.query<SystemFleetDetail, string>({
            queryFn: getSystemFleetAttributeQueryFn,
        }),
        updateSystemFleet: builder.mutation<SystemFleet, UpdatedFleetInput>({
            queryFn: async variables => {
                try {
                    const { data } = await client.graphql({
                        query: updateFleetById,
                        variables: {
                            input: variables,
                        },
                    });
                    return { data: SystemFleetSchema().parse(data.updateFleetById) };
                } catch (error) {
                    throw errorHandler(
                        `Unexpected error occurred while updating the fleet with id: ${variables.id}`,
                        error
                    );
                }
            },
        }),
        createSystemFleet: builder.mutation<SystemFleet, CreateFleetInput>({
            queryFn: async variables => {
                try {
                    const { data } = await client.graphql({
                        query: createFleet,
                        variables: {
                            input: variables,
                        },
                    });
                    return { data: SystemFleetSchema().parse(data.createFleet) };
                } catch (error) {
                    throw errorHandler(
                        `Unexpected error occurred while creating the fleet with id: ${variables.name}`,
                        error
                    );
                }
            },
        }),
    }),
});

export const {
    useListSystemFleetsQuery,
    useGetSystemFleetsAttributesQuery,
    useUpdateSystemFleetMutation,
    useCreateSystemFleetMutation,
} = systemFleetsApi;
